import {
  OnboardingStep,
  getOnboardingSteps,
} from '@/constants/onboardingSteps';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useAuthStore } from '@/stores/useAuthStore';
import { useOnboardingStore } from '@/stores/useOnboardingStore';
import '@/styles/onboarding.css';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Joyride, { CallBackProps } from 'react-joyride';
import { useNavigate } from 'react-router-dom';

interface OnboardingTourProps {
  cardSlugForTour?: string;
  isCardFetching?: boolean;
  selectedMenuItem?: number;
  setSelectedMenuItem?: (id: number) => void;
}

export const OnboardingTour = ({
  cardSlugForTour,
  isCardFetching,
  selectedMenuItem,
  setSelectedMenuItem,
}: OnboardingTourProps) => {
  const { user, finishOnboarding } = useAuthStore();
  const {
    run,
    stepIndex,
    isNavigating,
    stopTour,
    startTour,
    setStepIndex,
    setIsNavigating,
    resetOnboardingStore,
  } = useOnboardingStore();
  const { t } = useTranslation('onboarding');
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 1023px)');

  // Helper function to check if onboarding is finished
  const isOnboardingFinished = useCallback(
    (finished: boolean | number | string): boolean => {
      return finished === true || finished === 1 || finished === '1';
    },
    [],
  );

  // Memoize steps to prevent unnecessary recalculations
  const steps = useMemo(() => getOnboardingSteps(t, isMobile), [t, isMobile]);

  // Memoize tour running condition
  const runTour = useMemo(
    () => run && !isNavigating && !isCardFetching,
    [run, isNavigating, isCardFetching],
  );

  // Auto-start onboarding if user hasn't finished it yet and has accepted RGPD
  useEffect(() => {
    if (
      user &&
      !isOnboardingFinished(user.finished_onboarding) &&
      !run &&
      user.rgpd
    ) {
      startTour();
    }
  }, [user, startTour, run, isOnboardingFinished]);

  useEffect(() => {
    const currentStep = steps[stepIndex] as OnboardingStep;
    if (
      currentStep &&
      typeof currentStep.requiresMenuItemId === 'number' &&
      selectedMenuItem !== currentStep.requiresMenuItemId
    ) {
      setSelectedMenuItem?.(currentStep.requiresMenuItemId);
      if (!isNavigating) {
        setIsNavigating(true);
      }
      return;
    }
    if (isNavigating) {
      const nextStep = steps[stepIndex];
      if (nextStep && typeof nextStep.target === 'string') {
        const checkElement = () => {
          if (document.querySelector(nextStep.target as string)) {
            setIsNavigating(false);
          } else {
            setTimeout(checkElement, 100);
          }
        };
        checkElement();
      }
    }
  }, [
    isNavigating,
    stepIndex,
    steps,
    setIsNavigating,
    selectedMenuItem,
    setSelectedMenuItem,
  ]);

  // Optimized callback function with useCallback
  const handleJoyrideCallback = useCallback(
    async (data: CallBackProps) => {
      const { status, action, index, step } = data;
      const finishedStatuses: string[] = ['finished', 'skipped'];
      const currentStep = step as OnboardingStep;

      // Handle tour completion (finished, skipped, or closed)
      if (
        finishedStatuses.includes(status) ||
        action === 'close' ||
        action === 'skip'
      ) {
        stopTour();
        resetOnboardingStore();
        await finishOnboarding();
        navigate('/');
        return;
      }

      if (data.type === 'step:after') {
        const nextStepIndex = index + (action === 'next' ? 1 : -1);

        // Navigate to card update page
        if (currentStep.target === '#modify-card-button' && action === 'next') {
          if (cardSlugForTour) {
            setIsNavigating(true);
            setStepIndex(nextStepIndex);
            navigate(`cards/${cardSlugForTour}`);
          } else {
            stopTour();
            resetOnboardingStore();
            await finishOnboarding();
            navigate('/');
          }
          return;
        }

        // Handle mobile menu trigger step - just advance without clicking
        if (
          currentStep.target === '#mobile-menu-trigger' &&
          action === 'next'
        ) {
          setStepIndex(nextStepIndex);
          return;
        }

        // Handle final completion step - more robust detection
        if (
          currentStep.placement === 'center' &&
          currentStep.hideCloseButton &&
          currentStep.hideSkipButton &&
          stepIndex === steps.length - 1 &&
          action === 'next'
        ) {
          stopTour();
          resetOnboardingStore();
          await finishOnboarding();
          navigate('/');
          return;
        }

        setStepIndex(nextStepIndex);
      }
    },
    [
      stopTour,
      resetOnboardingStore,
      finishOnboarding,
      cardSlugForTour,
      setIsNavigating,
      setStepIndex,
      navigate,
      steps.length,
      stepIndex,
    ],
  );

  // Early return if user shouldn't see onboarding
  if (!user || isOnboardingFinished(user.finished_onboarding) || !user.rgpd) {
    // Stop the tour and reset store if it's still running but user has finished onboarding or hasn't accepted RGPD
    if (run) {
      stopTour();
      resetOnboardingStore();
    }
    return null;
  }

  // Additional safety check - don't show if tour is not supposed to run
  if (!runTour && !run) {
    return null;
  }

  return (
    <Joyride
      run={runTour}
      steps={steps}
      stepIndex={stepIndex}
      continuous
      showSkipButton
      hideCloseButton
      disableCloseOnEsc
      disableOverlayClose
      callback={handleJoyrideCallback}
      locale={{
        back: t('buttons.back'),
        close: t('buttons.close'),
        last: t('buttons.finish'),
        next: t('buttons.next'),
        skip: t('buttons.skip'),
      }}
      styles={{
        options: {
          primaryColor: '#00A3FF',
          zIndex: 10000,
        },
        overlay: {
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          ...(isMobile ? {} : { backdropFilter: 'blur(2px)' }),
        },
        spotlight: {
          borderRadius: '12px',
          boxShadow: '0 0 0 2px #00A3FF, 0 0 0 4px rgba(0, 163, 255, 0.3)',
        },
        tooltip: {
          backgroundColor: '#ffffff',
          borderRadius: '16px',
          padding: '20px',
          boxShadow:
            '0 20px 60px rgba(0, 0, 0, 0.15), 0 8px 25px rgba(0, 0, 0, 0.1)',
          border: '1px solid rgba(0, 163, 255, 0.1)',
          maxWidth: '350px',
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        },
        tooltipContainer: {
          textAlign: 'left',
          lineHeight: '1.5',
        },
        tooltipTitle: {
          fontSize: '18px',
          fontWeight: '600',
          color: '#1a1a1a',
          marginBottom: '8px',
          lineHeight: '1.3',
        },
        tooltipContent: {
          fontSize: '14px',
          color: '#4a5568',
          lineHeight: '1.6',
          marginBottom: '16px',
        },
        tooltipFooter: {
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          marginTop: '20px',
          paddingTop: '16px',
          borderTop: '1px solid #e2e8f0',
          position: 'relative',
        },
        tooltipFooterSpacer: {
          flex: '1',
        },
        buttonNext: {
          backgroundColor: '#00A3FF',
          borderRadius: '8px',
          border: 'none',
          color: '#ffffff',
          fontSize: '14px',
          fontWeight: '500',
          padding: '10px 20px',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          boxShadow: '0 2px 8px rgba(0, 163, 255, 0.3)',
        },
        buttonBack: {
          display: 'none', // Hide back button completely
        },
        buttonSkip: {
          backgroundColor: 'transparent',
          border: 'none',
          color: '#6b7280',
          fontSize: '13px',
          fontWeight: '400',
          padding: '8px 12px',
          cursor: 'pointer',
          textDecoration: 'underline',
          transition: 'all 0.2s ease',
          position: 'absolute',
          left: '0',
          top: '16px',
        },
        beacon: {
          backgroundColor: '#00A3FF',
          borderRadius: '50%',
          boxShadow: '0 0 0 4px rgba(0, 163, 255, 0.3)',
          animation: 'joyride-beacon 2s infinite',
        },
        beaconInner: {
          backgroundColor: '#ffffff',
          borderRadius: '50%',
          opacity: 0.7,
        },
        beaconOuter: {
          backgroundColor: '#00A3FF',
          borderRadius: '50%',
          opacity: 0.9,
        },
      }}
    />
  );
};
